import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Search, Settings, ArrowDownToLine, ArrowUpFromLine, DatabaseZap, Puzzle, Eye, EyeOff,
  User, Play, Lightbulb, Globe, SquareTerminal, LayoutGrid, Check, RefreshCw, Loader2, KeyRound, FileSearch, AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";

// ─── Tokens (shared with AiConsole) ───────────────────────────────────────────
const blue   = "var(--lyra-color-primary)";
const blueBg = "var(--app-primary-soft)";
const bord   = "var(--lyra-color-border)";
const tx     = "var(--lyra-color-text)";
const mu     = "var(--app-text-3)";
const su     = "var(--lyra-color-text-muted)";
const panel  = "var(--lyra-color-surface-2)";

const API_BASE = process.env.REACT_APP_API_URL ?? "";
const authHeaders = () => {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

// ─── Formatting ───────────────────────────────────────────────────────────────
const fmtCtx = n => {
  if (!n) return "—";
  if (n >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  return `${Math.round(n / 1000)}K`;
};
const fmtPrice = n => (n == null ? "—" : `$${+Number(n).toFixed(4)}`);
const keyOf    = (providerId, modelId) => `${providerId}:${modelId}`;

// ─── Live catalog hook ────────────────────────────────────────────────────────
function useLiveCatalog() {
  const [state, setState] = useState({ loading: true, error: null, providers: [], source: null, fetchedAt: null });
  const load = useCallback(async (refresh = false) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const r = await fetch(`${API_BASE}/api/models/live${refresh ? "?refresh=1" : ""}`, { headers: authHeaders() });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || "Failed to load models");
      setState({ loading: false, error: null, providers: d.providers, source: d.source, fetchedAt: d.fetchedAt });
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: e.message }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

// ─── Atoms ────────────────────────────────────────────────────────────────────
function Logo({ provider, size = 34 }) {
  const [failed, setFailed] = useState(false);
  const name = provider?.name || provider?.providerName || "?";
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "var(--lyra-color-surface)", border: "1px solid " + bord, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
      {provider?.logo && !failed
        ? <img src={provider.logo} alt="" onError={() => setFailed(true)} style={{ width: size * 0.58, height: size * 0.58, objectFit: "contain" }} />
        : <span style={{ fontSize: size * 0.4, fontWeight: 700, color: su }}>{name[0]}</span>}
    </div>
  );
}

function Switch({ on, busy, onChange }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); if (!busy) onChange(); }}
      aria-pressed={on}
      style={{ position: "relative", width: 36, height: 20, borderRadius: 999, border: "none", padding: 0, flexShrink: 0, cursor: busy ? "wait" : "pointer", background: on ? blue : "#4B5563", transition: "background .2s", opacity: busy ? 0.7 : 1 }}
    >
      <span style={{ position: "absolute", top: 3, left: on ? 19 : 3, width: 14, height: 14, borderRadius: "50%", background: on ? "var(--lyra-color-surface)" : "#2F3640", boxShadow: "0 1px 3px rgba(0,0,0,.3)", transition: "left .2s", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {busy && <Loader2 size={9} color={on ? blue : "var(--lyra-color-surface)"} style={{ animation: "spin .7s linear infinite" }} />}
      </span>
    </button>
  );
}

function FeatureChip({ icon: Icon, label, tint = "var(--app-surface-3)", color = blue }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: tx, minWidth: 0 }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, background: tint, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={15} />
      </span>
      <span style={{ whiteSpace: "nowrap" }}>{label}</span>
    </div>
  );
}

// ─── Overview / Parameters panel ──────────────────────────────────────────────
function DetailRow({ label, hint, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 12, padding: "9px 0", alignItems: "start", fontSize: 13 }}>
      <div style={{ color: su }}>{label}{hint && <span style={{ fontSize: 11.5, marginLeft: 5, color: mu }}>{hint}</span>}</div>
      <div style={{ color: tx, minWidth: 0, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

function ModelDetail({ model, isDefault, busy, onSetDefault }) {
  const [tab, setTab] = useState("overview");
  useEffect(() => { setTab("overview"); }, [model?.providerId, model?.id]);

  if (!model) {
    return <div style={{ color: su, fontSize: 13, padding: 40, textAlign: "center" }}>Select a model to see its details.</div>;
  }
  const f = model.features || {};
  const p = model.pricing;

  return (
    <>
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Logo provider={{ logo: model._logo, name: model.providerName }} size={34} />
        <div style={{ fontSize: 16, fontWeight: 700, color: tx }}>{model.name}</div>
        {isDefault && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 8, background: "var(--app-surface-3)", color: "var(--lyra-color-success)" }}>
            <Check size={13} /> Default
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", borderRadius: 12, overflow: "hidden", background: blue }}>
          <button onClick={onSetDefault} disabled={busy || isDefault} aria-disabled={isDefault} style={{ opacity: isDefault ? 0.85 : 1, display: "flex", alignItems: "center", gap: 9, padding: "0 20px", height: 46, border: "none", background: "none", color: "var(--app-on-accent)", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : isDefault ? "default" : "pointer" }}>
            {busy ? <Loader2 size={16} style={{ animation: "spin .7s linear infinite" }} /> : <Check size={17} />} {isDefault ? "Current default" : "Set as single default"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid " + bord, margin: "22px 0 18px" }}>
        {[["overview", "Overview"], ["parameters", "Parameters"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "12px 18px", border: "none", background: "none", fontSize: 13.5, fontWeight: 600, color: tab === id ? blue : su, cursor: "pointer", borderBottom: "2.5px solid " + (tab === id ? blue : "transparent"), marginBottom: -1 }}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          {model.description && <div style={{ fontSize: 13, color: "#3a3f3c", lineHeight: 1.5, marginBottom: 10 }}>{model.description}</div>}
          <DetailRow label="Model ID"><span style={{ fontWeight: 500 }}>{model.id}</span></DetailRow>
          <DetailRow label="Provider">{model.providerName}</DetailRow>
          <DetailRow label="Context length">{model.contextLength ? model.contextLength.toLocaleString() : "—"}</DetailRow>
          <DetailRow label="API type">{model.apiType}</DetailRow>
          <DetailRow label="Release date">{model.releaseDate || "—"}</DetailRow>
          <DetailRow label="Knowledge cutoff">{model.knowledgeCutoff || "—"}</DetailRow>
          <DetailRow label="Pricing" hint="/1M tokens">
            {p ? (
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                <span title="Input" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500 }}><ArrowDownToLine size={14} color={su} />{fmtPrice(p.input)}</span>
                <span title="Output" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500 }}><ArrowUpFromLine size={14} color={su} />{fmtPrice(p.output)}</span>
                {p.cacheWrite != null && <span title="Cache write" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500 }}><DatabaseZap size={14} color={su} />{fmtPrice(p.cacheWrite)}</span>}
                {p.cacheRead != null && <span title="Cache read" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500 }}><RefreshCw size={14} color={su} />{fmtPrice(p.cacheRead)}</span>}
              </div>
            ) : "—"}
          </DetailRow>
          <DetailRow label="Features">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))", gap: "12px 14px" }}>
              {f.plugins && <FeatureChip icon={Puzzle} label="Plugins" />}
              {f.vision && <FeatureChip icon={Eye} label="Vision" />}
              {f.promptCaching && <FeatureChip icon={DatabaseZap} label="Prompt caching" />}
              {f.systemRole && <FeatureChip icon={User} label="System role" />}
              {f.streaming && <FeatureChip icon={Play} label="Streaming" />}
              {f.thinking && <FeatureChip icon={Lightbulb} label="Thinking mode" />}
              {f.structuredOutput && <FeatureChip icon={FileSearch} label="Structured output" />}
            </div>
          </DetailRow>
          {f.tools?.length > 0 && (
            <DetailRow label="Provider tools">
              <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
                {f.tools.map(t => <FeatureChip key={t} icon={/Browser/.test(t) ? Globe : /Search/.test(t) ? FileSearch : SquareTerminal} label={t} tint="var(--app-surface-3)" color="var(--lyra-color-success)" />)}
              </div>
            </DetailRow>
          )}
        </div>
      )}

      {tab === "parameters" && (
        <div>
          <DetailRow label="Max output tokens">{model.maxOutput ? model.maxOutput.toLocaleString() : "—"}</DetailRow>
          <DetailRow label="Input modalities">{(model.modalities?.input || []).join(", ") || "text"}</DetailRow>
          <DetailRow label="Output modalities">{(model.modalities?.output || []).join(", ") || "text"}</DetailRow>
          <DetailRow label="Temperature">{model.temperature ? "Adjustable" : "Fixed by provider"}</DetailRow>
          <DetailRow label="Reasoning">
            {!f.thinking ? "Not supported" : model.reasoningOptions?.length
              ? model.reasoningOptions.map((o, i) => (
                <div key={i}>{o.type === "effort" ? `Effort: ${o.values.join(" · ")}` : o.type === "budget_tokens" ? `Token budget${o.min ? ` (min ${o.min.toLocaleString()})` : ""}` : o.type}</div>
              )) : "Supported"}
          </DetailRow>
          <DetailRow label="Structured output">{f.structuredOutput ? "Supported" : "Not supported"}</DetailRow>
          <DetailRow label="Open weights">{f.openWeights ? "Yes" : "No"}</DetailRow>
        </div>
      )}
    </>
  );
}

// ─── Models tab ───────────────────────────────────────────────────────────────
export function ModelsTab({
  rawModels, customApiKeys, selectedMode, setSelectedMode, selectedModelId, setSelectedModelId,
  refreshModels, onOpenKeys,
}) {
  const { loading, error, providers, source, fetchedAt, reload } = useLiveCatalog();
  const [activeProv, setActiveProv] = useState("all");
  const [query, setQuery]   = useState("");
  const [selected, setSelected] = useState(null);       // "provider:modelId"
  const [pending, setPending]   = useState(() => new Set());

  // User-added models (these are what the runtime actually uses) keyed by provider:modelId
  const userModels = useMemo(() => rawModels.filter(m => m.isUserAdded), [rawModels]);
  const enabledMap = useMemo(() => {
    const map = new Map();
    for (const m of userModels) {
      map.set(keyOf(m.provider, m.modelId || m.name), m);
      map.set(keyOf(m.provider, m.name), m);
    }
    return map;
  }, [userModels]);

  // Catalog + "Custom" bucket for user models that don't exist in the live registry
  const allProviders = useMemo(() => {
    const decorated = providers.map(p => ({
      ...p, models: p.models.map(m => ({ ...m, _logo: p.logo, _systemId: p.systemId })),
    }));
    const known = new Set(decorated.flatMap(p => p.models.flatMap(m => [keyOf(p.id, m.id), keyOf(p.id, m.name)])));
    const orphans = userModels.filter(m => !known.has(keyOf(m.provider, m.modelId || m.name)) && !known.has(keyOf(m.provider, m.name)));
    if (orphans.length) {
      decorated.push({
        id: "custom", name: "Custom", logo: null, systemId: null,
        models: orphans.map(m => ({
          id: m.modelId || m.name, name: m.name, providerId: m.provider, providerName: m.providerLabel || m.provider,
          description: m.description || "", contextLength: m.contextK ? m.contextK * 1000 : null,
          apiType: "custom", features: { streaming: true, systemRole: true, tools: [] }, _logo: null, _custom: true,
        })),
      });
    }
    return decorated;
  }, [providers, userModels]);

  const entryFor = useCallback(m => enabledMap.get(keyOf(m.providerId, m.id)) || enabledMap.get(keyOf(m.providerId, m.name)) || null, [enabledMap]);

  const visibleModels = useMemo(() => {
    const pool = activeProv === "all" ? allProviders.flatMap(p => p.models) : (allProviders.find(p => p.id === activeProv)?.models || []);
    const sorted = activeProv === "all"
      ? [...pool].sort((a, b) => (b.releaseDate || "").localeCompare(a.releaseDate || ""))
      : pool;
    const q = query.trim().toLowerCase();
    return q ? sorted.filter(m => m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q)) : sorted;
  }, [allProviders, activeProv, query]);

  const totalModels = useMemo(() => allProviders.reduce((n, p) => n + p.models.length, 0), [allProviders]);
  const enabledIn = p => p.models.filter(m => entryFor(m)).length;
  const totalEnabled = allProviders.reduce((n, p) => n + enabledIn(p), 0);

  // Keep a valid selection when the filtered list changes
  const selectedModel = useMemo(() => {
    const hit = visibleModels.find(m => keyOf(m.providerId, m.id) === selected);
    return hit || visibleModels[0] || null;
  }, [visibleModels, selected]);

  async function enable(m) {
    const prov = allProviders.find(p => p.id === m.providerId);
    const res = await fetch(`${API_BASE}/api/models/user-models`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        providerId: m.providerId, providerName: m.providerName, modelName: m.name, modelId: m.id,
        systemId: prov?.systemId || m.providerId, badge: "KEY",
        desc: (m.description || "").slice(0, 200),
        contextK: m.contextLength ? Math.round(m.contextLength / 1000) : null,
        keyUrl: prov?.keyUrl || "", keyLabel: `${m.providerName} console`,
      }),
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.error || "Could not enable model");
    return String(d.model._id);
  }

  async function disable(entry) {
    const res = await fetch(`${API_BASE}/api/models/user-models/${entry.id}`, { method: "DELETE", headers: authHeaders() });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.error || "Could not disable model");
    if (selectedModelId === entry.id) setSelectedModelId(null);
  }

  async function withPending(m, fn) {
    const k = keyOf(m.providerId, m.id);
    setPending(s => new Set(s).add(k));
    try { return await fn(); }
    catch (e) { toast.error(e.message); }
    finally { setPending(s => { const n = new Set(s); n.delete(k); return n; }); }
  }

  const toggle = m => withPending(m, async () => {
    const entry = entryFor(m);
    if (entry) { await disable(entry); }
    else {
      await enable(m);
      const prov = allProviders.find(p => p.id === m.providerId);
      if (prov?.systemId && !customApiKeys[prov.systemId]) toast(`Enabled — add a ${prov.name} API key to start using it.`, { icon: "🔑" });
    }
    await refreshModels();
  });

  const setDefault = m => withPending(m, async () => {
    const entry = entryFor(m);
    const id = entry ? entry.id : await enable(m);
    if (!entry) await refreshModels();
    setSelectedMode("manual");
    setSelectedModelId(id);
    toast.success(`${m.name} is now the single default`);
  });

  const isDefault = m => { const e = entryFor(m); return !!e && selectedMode === "manual" && selectedModelId === e.id; };

  const providerDot = p => {
    const hasKey = p.systemId && customApiKeys[p.systemId];
    if (hasKey) return "var(--lyra-color-success)";
    return enabledIn(p) > 0 ? "var(--lyra-color-warning)" : "#c9ccc9";
  };

  // The model every request uses (manual) or the router (auto), shown plainly with a switch between the two.
  const defaultModel = (() => {
    if (selectedMode !== "manual" || !selectedModelId) return null;
    const hit = rawModels.find(x => String(x._id ?? x.id) === String(selectedModelId));
    return hit ? { name: hit.name || hit.modelName, provider: hit.providerLabel || hit.provider } : { name: String(selectedModelId), provider: "" };
  })();
  const canManual = !!selectedModelId;

  return (
    <>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 14px", marginBottom: 14, borderRadius: 12, background: panel, border: "0.5px solid " + bord }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: su, letterSpacing: ".06em", textTransform: "uppercase" }}>Default model</div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: tx, marginTop: 2 }}>
          {defaultModel ? <>{defaultModel.name}{defaultModel.provider ? <span style={{ fontWeight: 400, color: su }}> · {defaultModel.provider}</span> : null}</> : "GPT-4o mini · OpenAI · Provided by Lyra"}
        </div>
      </div>
      {!defaultModel && <div style={{ fontSize: 11.5, color: su }}>API key securely managed by Lyra · Free trial limits apply</div>}
      <div role="group" aria-label="How the model is chosen" style={{ display: "inline-flex", padding: 3, borderRadius: 10, background: "var(--app-surface-3)" }}>
        {[["auto", "Auto", true, "Lyra chooses the model for each request"], ["manual", "Single default", canManual, canManual ? "Always use the default model" : "Open a model and choose “Set as single default” first"]].map(([id, label, enabled, tip]) => (
          <button key={id} type="button" onClick={() => enabled && setSelectedMode(id)} disabled={!enabled} aria-pressed={selectedMode === id} title={tip}
            style={{ padding: "5px 12px", borderRadius: 8, border: "none", fontSize: 12.5, fontWeight: 600, cursor: enabled ? "pointer" : "not-allowed", background: selectedMode === id ? "var(--lyra-color-surface)" : "transparent", color: selectedMode === id ? tx : su, opacity: enabled ? 1 : 0.5, boxShadow: selectedMode === id ? "0 1px 2px rgba(0,0,0,.15)" : "none" }}>
            {label}
          </button>
        ))}
      </div>
    </div>
    <div className="mdl-grid">
      {/* ── Providers ── */}
      <div className="mdl-col mdl-providers">
        {[{ id: "all", name: "All models", models: { length: totalModels }, _enabled: totalEnabled }, ...allProviders].map(p => {
          const active = activeProv === p.id;
          const en = p.id === "all" ? p._enabled : enabledIn(p);
          return (
            <div key={p.id} onClick={() => { setActiveProv(p.id); setQuery(""); setSelected(null); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: "pointer", background: active ? "var(--app-surface-3)" : "transparent", transition: "background .12s" }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = panel; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
              {p.id === "all"
                ? <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--app-surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><LayoutGrid size={14} color={su} /></div>
                : <Logo provider={p} size={30} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                <div style={{ fontSize: 12, color: su }}>{en} / {p.models.length} enabled</div>
              </div>
              {p.id !== "all" && p.id !== "custom" && (
                <>
                  <span title={customApiKeys[p.systemId] ? "API key configured" : en ? "API key missing" : "Not enabled"} style={{ width: 8, height: 8, borderRadius: "50%", background: providerDot(p), flexShrink: 0 }} />
                  <button title={`${p.name} API key`} onClick={e => { e.stopPropagation(); onOpenKeys(p.id); }} style={{ border: "none", background: "none", padding: 4, cursor: "pointer", color: tx, display: "flex" }}>
                    <Settings size={15} />
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Model list ── */}
      <div className="mdl-col mdl-list">
        <div style={{ position: "relative" }}>
          <Search size={14} color={su} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search..."
            style={{ width: "100%", height: 38, borderRadius: 10, border: "none", background: "var(--app-surface-3)", padding: "0 12px 0 36px", fontSize: 13.5, color: tx, outline: "none", fontFamily: "inherit" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "14px 4px 6px", fontSize: 12, color: su }}>
          <span>Showing {visibleModels.length} of {activeProv === "all" ? totalModels : (allProviders.find(p => p.id === activeProv)?.models.length ?? 0)} models</span>
          <button onClick={() => reload(true).then(() => toast.success("Model list refreshed"))} disabled={loading} title={fetchedAt ? `Synced ${new Date(fetchedAt).toLocaleString()} · ${source}` : "Sync latest models"}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "none", cursor: "pointer", color: su, fontSize: 12.5, padding: 0 }}>
            <RefreshCw size={12} style={loading ? { animation: "spin .7s linear infinite" } : undefined} /> {loading ? "Syncing" : "Sync"}
          </button>
        </div>
        {source === "static" && !loading && (
          <div style={{ fontSize: 12, color: "var(--lyra-color-warning)", background: "var(--lyra-color-warning-bg)", border: "1px solid var(--app-warning-border)", borderRadius: 8, padding: "6px 10px", margin: "0 4px 8px" }}>
            Couldn't reach the live registry — showing the built-in list.
          </div>
        )}
        <div className="mdl-scroll">
          {loading && !providers.length && <div style={{ padding: 30, textAlign: "center", color: su, fontSize: 12.5 }}><Loader2 size={18} style={{ animation: "spin .7s linear infinite" }} /><div style={{ marginTop: 8 }}>Loading latest models…</div></div>}
          {error && !providers.length && <div style={{ padding: 20, color: "var(--lyra-color-danger)", fontSize: 12.5 }}>{error}</div>}
          {visibleModels.map(m => {
            const k = keyOf(m.providerId, m.id);
            const on = !!entryFor(m);
            const sel = selectedModel && keyOf(selectedModel.providerId, selectedModel.id) === k;
            return (
              <div key={k} onClick={() => setSelected(k)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, cursor: "pointer", background: sel ? "var(--app-surface-3)" : "transparent", contentVisibility: "auto", containIntrinsicSize: "0 72px" }}
                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = panel; }}
                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}>
                <Logo provider={{ logo: m._logo, name: m.providerName }} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: tx, display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</span>
                    {m.isNew && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--lyra-color-success)", letterSpacing: ".03em", flexShrink: 0 }}>NEW</span>}
                    {isDefault(m) && <span title="Used for every request unless you pick another model" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", padding: "1px 7px", borderRadius: 99, background: "var(--app-primary-soft)", color: "var(--app-primary-deep)", border: "0.5px solid var(--app-primary-soft-2)", flexShrink: 0 }}>DEFAULT</span>}
                  </div>
                  <div style={{ fontSize: 12, color: su, marginTop: 2 }}>{fmtCtx(m.contextLength)}</div>
                </div>
                <Switch on={on} busy={pending.has(k)} onChange={() => toggle(m)} />
              </div>
            );
          })}
          {!loading && visibleModels.length === 0 && !error && (
            <div style={{ padding: 30, textAlign: "center", color: su, fontSize: 12.5 }}>No models match “{query}”.</div>
          )}
        </div>
      </div>

      {/* ── Overview ── */}
      <div className="mdl-col mdl-detail">
        <ModelDetail
          model={selectedModel}
          isDefault={selectedModel ? isDefault(selectedModel) : false}
          busy={selectedModel ? pending.has(keyOf(selectedModel.providerId, selectedModel.id)) : false}
          onSetDefault={() => selectedModel && setDefault(selectedModel)}
        />
      </div>
    </div>
    </>
  );
}

// ─── API keys tab ─────────────────────────────────────────────────────────────
export function ApiKeysTab({ customApiKeys, setCustomKey, focusProvider }) {
  const { loading, providers } = useLiveCatalog();
  const [drafts, setDrafts]   = useState({});
  const [shown, setShown]     = useState({});
  const [saving, setSaving]   = useState(null);
  const [testing, setTesting] = useState(null);      // provider id being checked
  const [verdict, setVerdict] = useState({});         // provider id -> { ok, message }
  const rowRefs = useRef({});

  useEffect(() => {
    if (focusProvider && rowRefs.current[focusProvider]) rowRefs.current[focusProvider].scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusProvider, providers.length]);

  async function save(p, value) {
    setSaving(p.id);
    try {
      await setCustomKey(p.systemId, value);
      setDrafts(d => { const n = { ...d }; delete n[p.id]; return n; });
      toast.success(value ? `${p.name} key saved` : `${p.name} key removed`);
    } catch (err) {
      const detail = err?.response?.data?.details?.[p.systemId] || err?.response?.data?.error || err.message;
      toast.error(detail || "Failed to save key");
    } finally { setSaving(null); }
  }

  // Checks a key against the provider without saving it (the key in the box, or the saved one).
  async function test(p, value) {
    if (!value) { toast.error(`Paste a ${p.name} key first`); return; }
    setTesting(p.id);
    try {
      const res = await fetch(`${API_BASE}/api/usage/test-key`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ modelId: p.systemId, apiKey: value }) });
      const d = await res.json().catch(() => ({}));
      const ok = res.ok && d.ok !== false && !d.error;
      const message = ok ? "Key works" : d.error === "Unknown model" ? "This provider cannot be tested separately; Save also checks the key" : (d.error || d.message || "The provider rejected this key");
      setVerdict(v => ({ ...v, [p.id]: { ok, message } }));
      (ok ? toast.success : toast.error)(`${p.name}: ${message}`);
    } catch (e) {
      setVerdict(v => ({ ...v, [p.id]: { ok: false, message: "Could not reach the server to test" } }));
      toast.error("Could not reach the server to test");
    } finally { setTesting(null); }
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ fontSize: 13, color: su, marginBottom: 18 }}>
        Keys are verified against the provider before saving and apply to every model from that provider.
      </div>
      {loading && !providers.length && <Loader2 size={18} color={su} style={{ animation: "spin .7s linear infinite" }} />}
      {providers.map(p => {
        const saved = customApiKeys[p.systemId] || "";
        const draft = drafts[p.id] ?? saved;
        const dirty = draft !== saved;
        const focused = focusProvider === p.id;
        return (
          <div key={p.id} ref={el => { rowRefs.current[p.id] = el; }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 8, borderRadius: 12, background: focused ? blueBg : "var(--lyra-color-surface)", border: "1px solid " + (focused ? "var(--app-primary-soft-2)" : bord) }}>
            <Logo provider={p} size={30} />
            <div style={{ width: 120, flexShrink: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>{p.name}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: saved ? "var(--lyra-color-success)" : su }}>{saved ? "● Configured" : "Not set"}</div>
            </div>
            <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
              <input type={shown[p.id] ? "text" : "password"} value={draft} placeholder="Paste API key…"
                onChange={e => setDrafts(d => ({ ...d, [p.id]: e.target.value }))}
                style={{ width: "100%", height: 34, borderRadius: 9, border: "1px solid " + (dirty ? blue : bord), padding: "0 36px 0 10px", fontSize: 12.5, outline: "none", background: "var(--lyra-color-surface)", color: tx, fontFamily: "inherit" }} />
              <button onClick={() => setShown(s => ({ ...s, [p.id]: !s[p.id] }))} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", cursor: "pointer", color: mu, display: "flex", padding: 2 }}>
                {shown[p.id] ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <button disabled={!draft.trim() || testing === p.id} onClick={() => test(p, draft.trim())} title="Check this key with the provider without saving it"
              style={{ height: 34, padding: "0 12px", borderRadius: 9, border: "1px solid " + bord, background: verdict[p.id] ? (verdict[p.id].ok ? "var(--lyra-color-success-bg)" : "var(--lyra-color-danger-bg)") : "var(--lyra-color-surface)", color: verdict[p.id] ? (verdict[p.id].ok ? "var(--lyra-color-success)" : "var(--lyra-color-danger)") : tx, fontWeight: 600, fontSize: 12.5, cursor: !draft.trim() ? "not-allowed" : "pointer", opacity: !draft.trim() ? 0.5 : 1, display: "flex", alignItems: "center", gap: 5 }}>
              {testing === p.id ? <Loader2 size={13} style={{ animation: "spin .7s linear infinite" }} /> : verdict[p.id] ? (verdict[p.id].ok ? <Check size={13} /> : <AlertTriangle size={13} />) : null} {testing === p.id ? "Testing" : verdict[p.id] ? (verdict[p.id].ok ? "Works" : "Failed") : "Test"}
            </button>
            <button disabled={!dirty || saving === p.id} onClick={() => save(p, draft.trim())}
              style={{ height: 34, padding: "0 14px", borderRadius: 9, border: "none", background: dirty ? blue : "var(--app-surface-3)", color: dirty ? "var(--lyra-color-surface)" : mu, fontWeight: 600, fontSize: 12.5, cursor: dirty ? "pointer" : "default", display: "flex", alignItems: "center", gap: 6 }}>
              {saving === p.id ? <Loader2 size={14} style={{ animation: "spin .7s linear infinite" }} /> : <Check size={14} />} Save
            </button>
            <a href={p.keyUrl} target="_blank" rel="noopener noreferrer" title={`Get a ${p.name} key`}
              style={{ height: 34, width: 34, borderRadius: 9, border: "1px solid " + bord, display: "flex", alignItems: "center", justifyContent: "center", color: su, flexShrink: 0 }}>
              <KeyRound size={15} />
            </a>
          </div>
        );
      })}
    </div>
  );
}

export const modelsTabCss = `
  .mdl-grid { display:grid; grid-template-columns: 300px 380px minmax(0,1fr); gap:20px; height:calc(100vh - 262px); min-height:520px; }
  .mdl-col { min-height:0; }
  .mdl-providers { overflow-y:auto; padding-right:6px; }
  .mdl-list { display:flex; flex-direction:column; }
  .mdl-scroll { flex:1; overflow-y:auto; min-height:0; padding-right:4px; }
  .mdl-detail { background:${panel}; border-radius:18px; padding:20px 22px; overflow-y:auto; }
  @media (max-width: 1180px) { .mdl-grid { grid-template-columns: 240px 320px minmax(0,1fr); } }
  @media (max-width: 960px) {
    .mdl-grid { grid-template-columns: 1fr; height:auto; }
    .mdl-providers { display:flex; overflow-x:auto; gap:6px; padding:0 0 6px; }
    .mdl-providers > div { flex:0 0 auto; }
    .mdl-scroll { max-height:420px; }
  }
`;
